import {Box, Button, DropdownItem} from '@viptech/react-components'
import sub from 'date-fns/sub'
import {capitalize} from 'lodash'
import moment from 'moment'
import {enqueueSnackbar} from 'notistack'
import {useEffect, useRef, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import DeleteIcon from 'src/images/DeleteIcon'
import DownloadIcon from 'src/images/DownloadIcon'
import {ResponsiveBox} from 'src/layouts/main-layout/LayoutBase'
import {fileDateFormat} from 'src/utilities/datetimeUtils'
import WebSocketStatus from '../../../../common/components/websocket-status/WebsocketStatus'
import {AdvancedFilters, FilterSelectedFinal} from '../../../../components/advancedFilters/AdvancedFilters'
import {TransparentButton} from '../../../../components/advancedFilters/AdvancedFiltersBase'
import CustomCalendar from '../../../../components/customCalendar/CustomCalendar'
import {SelectWithSearchComponent} from '../../../../components/selectWithSearchComponent/SelectWithSearchComponent'
import TopBar from '../../../../components/top-bar/TopBar'
import useEventsReport from '../../../../hooks/api/dashboard/useEventsReport'
import {WebSocketStatusesEnum} from '../../../../hooks/api/socket/useWebsocket'
import {useQueryParams} from '../../../../hooks/useQuery'
import {FilterEventsType, SearchEventsType} from '../../../../services/api'
import {TitleOnButtonResponsive} from '../../../lpr/LicensePlateBase'
import {ContainerFilters, FiltersSelectedContainer} from '../../AlarmsBase'
import {ListObjectEnum, ListObjectIdEnum} from '../../AlarmsFunctions'

const searchTypes = [
  {id: 'cameraName', label: 'Câmera'},
  {id: 'clientName', label: 'Cliente'},
  {id: 'id', label: 'ID'},
]

type Props = {
  filter: FilterEventsType
  setFilter: React.Dispatch<React.SetStateAction<FilterEventsType>>
  search: SearchEventsType
  setSearch: (search: SearchEventsType) => void
  webSocketConnectionStatus: WebSocketStatusesEnum
  forceConnect: () => Promise<boolean>
  forceDisconnect: () => void
}

const typeOfDetections: Array<{id: string; label: string}> = [
  {
    id: ListObjectIdEnum.OBJECT_DETECTION,
    label: ListObjectEnum.OBJECT_DETECTION,
  },
  {
    id: ListObjectIdEnum.LPR,
    label: ListObjectEnum.LPR,
  },
  {
    id: ListObjectIdEnum.LINE_CROSSING,
    label: ListObjectEnum.LINE_CROSSING,
  },
  {
    id: ListObjectIdEnum.FACIAL_RECOGNITION,
    label: ListObjectEnum.FACIAL_RECOGNITION,
  },
  {
    id: ListObjectIdEnum.SCENE_CHANGE,
    label: ListObjectEnum.SCENE_CHANGE,
  },
  {
    id: ListObjectIdEnum.SPEED,
    label: ListObjectEnum.SPEED,
  },
  {
    id: ListObjectIdEnum.EPI,
    label: ListObjectEnum.EPI,
  },
  {
    id: ListObjectIdEnum.HOODIE,
    label: ListObjectEnum.HOODIE,
  },
  {
    id: ListObjectIdEnum.SHOPPING_CART,
    label: ListObjectEnum.SHOPPING_CART,
  },
]

const initialStatusList = [
  {id: '1', label: 'Iniciado'},
  {id: '2', label: 'Processado'},
  {id: '3', label: 'Ignorado'},
  {id: '4', label: 'Expirado'},
  {id: '5', label: 'Erro'},
  {id: '6', label: 'Despachado'},
  {id: '7', label: 'Correto'},
  {id: '8', label: 'Incorreto'},
]

export const TobBarHeader = (props: Props) => {
  const {filter, setFilter, setSearch, search, webSocketConnectionStatus, forceConnect, forceDisconnect} = props

  const query = useQueryParams()
  const navigate = useNavigate()
  const location = useLocation()
  const {styledTheme} = usePersonalizedThemeByCompany()

  const [chosenFilters, setChosenFilters] = useState<FilterSelectedFinal[]>([])
  const [showMoreFilters, setShowMoreFilters] = useState(true)
  const [disabledSelectFilters, setDisabledSelectFilters] = useState(false)

  const [searchSelected, setSearchSelected] = useState<DropdownItem>({id: '', label: ''})
  const [inputAlarmSearch, setInputAlarmSearch] = useState<string>('')

  const [listStatusOfEvent, setListStatusOfEvent] = useState(initialStatusList)
  const [listTypeOfDetections, setListTypeOfDetections] = useState<DropdownItem[]>(typeOfDetections)
  const initialListTypeOfDetectionsMemory = useRef<DropdownItem[]>(typeOfDetections)

  const eventFilters = [
    {
      id: '1',
      label: 'Status',
      itemsOnMenu: listStatusOfEvent,
    },
    {
      id: '2',
      label: 'Tipo de Detecção',
      itemsOnMenu: listTypeOfDetections,
    },
  ]

  async function handleSearchInput() {
    if (searchSelected.id && inputAlarmSearch) {
      forceDisconnect()
      setSearch({
        [searchSelected.id]: inputAlarmSearch,
      })
    } else if (searchSelected.id && !inputAlarmSearch) {
      setSearch({[searchSelected.id]: undefined})
    }
  }

  useEffect(() => {
    handleSearchInput()
  }, [searchSelected, inputAlarmSearch])

  useEffect(() => {
    const hasCameraQuery = query.get('camera')
    const hasIdQuery = query.get('id')
    const dateTo = query.get('to')
    const dateFrom = query.get('from')

    if (dateTo && dateFrom) {
      setFilter({to: dateTo, from: dateFrom})
    }

    if (hasCameraQuery) {
      forceDisconnect()
      setSearchSelected({id: 'cameraName', label: 'Câmera'})
      setInputAlarmSearch(hasCameraQuery)
      setFilter({statuses: []})
      setShowMoreFilters(false)
    }
    if (hasIdQuery) {
      forceDisconnect()
      setSearchSelected({id: 'id', label: 'ID'})
      setInputAlarmSearch(hasIdQuery)
      setFilter({statuses: []})
      setShowMoreFilters(false)
    }
  }, [])

  useEffect(() => {
    setInitialFilterOnChosen()
  }, [])

  const setInitialFilterOnChosen = () => {
    if (!filter.statuses?.length) return
    const filterStatusDefault = filter.statuses.map((it) => {
      const newList = listStatusOfEvent.filter((item) => item.label !== it)
      setListStatusOfEvent(newList)
      return {
        id: '1',
        label: 'Status',
        itemsOnMenu: [],
        selectedItem: {
          id: '',
          label: capitalize(it.toLowerCase()),
        },
      }
    })
    setChosenFilters(filterStatusDefault)
  }

  const handleDateFilterStatus = async (isFilteringInPast: boolean) => {
    if (isFilteringInPast) {
      forceDisconnect()
    } else if (webSocketConnectionStatus === WebSocketStatusesEnum.DISCONNECTED && !inputAlarmSearch) {
      await forceConnect()
    }
  }

  const [initialDatesToShowOnCalendar, setInitalDatesToShowOnCalendar] = useState<[Date, Date]>([new Date(filter.from!), new Date(filter.to!)])

  const handleChangeTimeStart = (dates: [Date, Date]) => {
    let from = moment(dates[0]).format()
    let to = moment(dates[1]).format()
    if (dates[0] === dates[1]) {
      from = moment(dates[0].setHours(0, 0, 0, 0)).format()
      to = moment(dates[1].setHours(23, 59, 59, 59)).format()
    }
    setFilter({
      ...filter,
      from,
      to,
    })
    setInitalDatesToShowOnCalendar(dates)
    const withoutMiliTo = new Date(dates[1]).setMilliseconds(0)
    const withoutMiliNow = new Date().setMilliseconds(0)
    handleDateFilterStatus(dates[1] && dates[0] && withoutMiliTo < withoutMiliNow)
  }

  const isFilteringPastDate = () => {
    const withoutMiliTo = new Date(filter.to ?? '').setMilliseconds(0)
    const withoutMiliNow = new Date().setMilliseconds(0)
    return filter.from && filter.to && withoutMiliTo < withoutMiliNow
  }

  const onPressBackToLive = async () => {
    setChosenFilters([])
    setShowMoreFilters(false)
    setInputAlarmSearch('')
    setInitalDatesToShowOnCalendar([sub(new Date(), {minutes: 30}), new Date()])
    setFilter({companyIds: filter.companyIds, to: moment(sub(new Date(), {minutes: 30})).format(), from: moment(new Date()).format()})
    navigate(location.pathname, {replace: true})
    await forceConnect()
  }

  const handleOpenPartialFilters = (newFilter: FilterSelectedFinal) => {
    if (!listStatusOfEvent.length && newFilter.label === 'Status') return
    if (!listTypeOfDetections.length && newFilter.label === 'Tipo de Detecção') return
    setDisabledSelectFilters(true)

    const moreFilters = [...chosenFilters, newFilter]
    setChosenFilters(moreFilters)
    setShowMoreFilters(true)
  }

  const handleSetFiltersSelected = (value: DropdownItem, index: number) => {
    setDisabledSelectFilters(false)

    const newFilter = [...chosenFilters]
    newFilter[index].selectedItem = value
    const valuesNotSelectedOnStatusFilter = listStatusOfEvent.filter((it) => it.label !== value.label)
    setListStatusOfEvent(valuesNotSelectedOnStatusFilter)

    const valuesNotSelectedOnTypeFilter = listTypeOfDetections.filter((it) => it.label !== value.label)
    setListTypeOfDetections(valuesNotSelectedOnTypeFilter)

    newFilter.forEach((it) => {
      if (it.label === 'Status') it.itemsOnMenu = valuesNotSelectedOnStatusFilter
      else it.itemsOnMenu = valuesNotSelectedOnTypeFilter
    })
    setChosenFilters(newFilter)

    if (newFilter[index].label === 'Status') {
      setFilter({...filter, statuses: [...(filter.statuses ?? []), value.label.toUpperCase()]})
    } else if (newFilter[index].label === 'Tipo de Detecção') {
      setFilter({...filter, eventTypeIds: [value.id, ...(filter.eventTypeIds ?? [])]})
    }
  }

  const deleteFilter = async (index: number) => {
    const deleteItem = chosenFilters[index].selectedItem?.label
    if (!deleteItem) return

    if (chosenFilters[index].label === 'Status') {
      const deletedItemToIncludeAgainOnList = initialStatusList.find((it) => it.label === deleteItem)
      const statuses = filter.statuses?.filter((it) => it !== deleteItem.toUpperCase())
      setFilter({...filter, statuses})
      if (deletedItemToIncludeAgainOnList) setListStatusOfEvent((prev) => [...prev, deletedItemToIncludeAgainOnList])
    }

    if (chosenFilters[index].label === 'Tipo de Detecção') {
      const deletedItem = chosenFilters[index].selectedItem ?? initialListTypeOfDetectionsMemory.current.find((it) => it.label === deleteItem)!
      setListTypeOfDetections((prev) => [...prev, deletedItem])
      const eventTypeIds = filter.eventTypeIds?.filter((it) => it !== deletedItem.id)
      setFilter({...filter, eventTypeIds})
    }

    const deletedFilters = chosenFilters.filter((it) => it.selectedItem?.label !== deleteItem)
    setChosenFilters(deletedFilters)

    if (deletedFilters.length <= 0) {
      setShowMoreFilters(false)

      if (!isFilteringPastDate() && !inputAlarmSearch && webSocketConnectionStatus === WebSocketStatusesEnum.DISCONNECTED) await forceConnect()
    }
  }

  const deleteAllFilters = async () => {
    setFilter((old) => ({companyIds: filter.companyIds, from: old.from, to: old.to}))
    setDisabledSelectFilters(false)
    setShowMoreFilters(false)
    setChosenFilters([])
    setListStatusOfEvent(initialStatusList)
    setListTypeOfDetections(initialListTypeOfDetectionsMemory.current)

    if (!isFilteringPastDate() && !inputAlarmSearch && webSocketConnectionStatus === WebSocketStatusesEnum.DISCONNECTED) await forceConnect()
  }

  const clearEmptyFilter = () => {
    const newFilters = chosenFilters.filter((it) => it.selectedItem)
    setChosenFilters(newFilters)
    setDisabledSelectFilters(false)
  }

  const {isPending: isDownloadingReport, mutateAsync} = useEventsReport()

  const downloadReport = async () => {
    try {
      const {data} = await mutateAsync({
        search,
        filter: {
          companyIds: filter.companyIds,
          eventTypeIds: filter.eventTypeIds,
          cameraIds: filter.cameraIds,
          statuses: filter.statuses,
          statusCameras: filter.statusCameras,
          isOnline: filter.isOnline,
        },
        start: filter.from ?? moment(sub(new Date(), {minutes: 30})).format(),
        end: filter.to ?? moment(new Date()).format(),
      })

      const startDate = filter.from ? fileDateFormat(new Date(filter.from).toISOString().replaceAll('/', '-')) : undefined
      const endDate = filter.to ? fileDateFormat(new Date(filter.to).toISOString().replaceAll('/', '-')) : undefined
      const dateFileName = filter.from?.length && filter.to?.length ? `_${startDate}_${endDate}` : ''
      const blob = new Blob([data], {type: 'text/csv'})
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `relatório-eventos${dateFileName}.csv`
      a.click()
    } catch (error) {
      enqueueSnackbar(`Falha ao baixar relatório de eventos, por favor tente novamente mais tarde`, {variant: 'error'})
    }
  }

  return (
    <TopBar>
      <ResponsiveBox>
        <Box display='flex'>
          <SelectWithSearchComponent
            searchBy={searchTypes}
            setSearchSelected={setSearchSelected}
            searchSelected={searchSelected}
            inputValue={inputAlarmSearch}
            inputSearch={setInputAlarmSearch}
            filterBy={eventFilters}
            setSelectedFilter={handleOpenPartialFilters}
            disabledSelectFilter={disabledSelectFilters}
          />
        </Box>
        <Box display='flex' direction='row' columnGap='16px'>
          <WebSocketStatus status={webSocketConnectionStatus} onPressBackToLive={onPressBackToLive} onPressDisconnect={forceDisconnect} />
          <CustomCalendar initialDates={initialDatesToShowOnCalendar} setDate={(dates) => handleChangeTimeStart(dates)} />

          <Button
            variant='outlined'
            height='40px'
            hoverColor='none'
            isLoading={isDownloadingReport}
            loadingColor={styledTheme.colors.primary}
            disabled={isDownloadingReport}
            onClick={downloadReport}>
            <Box display='flex' columnGap='8px' align='center'>
              <DownloadIcon />
              <TitleOnButtonResponsive>Baixar</TitleOnButtonResponsive>
            </Box>
          </Button>
        </Box>
      </ResponsiveBox>

      <Box marginTop='15px' align='center' display='flex' justifyContent='center'>
        {showMoreFilters && (
          <ContainerFilters>
            <FiltersSelectedContainer>
              {chosenFilters.map((it, index) => {
                return (
                  <AdvancedFilters
                    key={index}
                    index={index}
                    filtersSelected={it}
                    setFiltersSelected={handleSetFiltersSelected}
                    deleteFilter={deleteFilter}
                    clearEmptyFilter={clearEmptyFilter}
                  />
                )
              })}
            </FiltersSelectedContainer>

            <TransparentButton onClick={() => deleteAllFilters()}>
              <Box display='flex' columnGap='10px' align='center'>
                <DeleteIcon />
                <p>Limpar Seleção</p>
              </Box>
            </TransparentButton>
          </ContainerFilters>
        )}
      </Box>
    </TopBar>
  )
}
